import { Link } from "react-router-dom";

const ARCHETYPES = [
  {
    name: "Explorer",
    tagline: "The Boundless Wanderer",
    desc: "Freedom. Challenge. The unknown.",
    image: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=600&q=80",
    color: "#E8A838"
  },
  {
    name: "Romantic",
    tagline: "The Eternal Lover",
    desc: "Connection. Beauty. Intimate moments.",
    image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/579df819394e67a807b0e29cb57896fa7d74a6896f385b69464ef110962b4a7e.png",
    color: "#E8738A"
  },
  {
    name: "Celebrator",
    tagline: "The Joy Architect",
    desc: "Music. Energy. Shared euphoria.",
    image: "https://images.pexels.com/photos/35593189/pexels-photo-35593189.jpeg?auto=compress&cs=tinysrgb&w=600",
    color: "#A855F7"
  },
  {
    name: "Seeker",
    tagline: "The Deep Diver",
    desc: "Meaning. Depth. Inner clarity.",
    image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/f4b1db47b17139414763c35dc51264fe7092429b2f57e752a88eaa1779a9c934.png",
    color: "#22C55E"
  },
  {
    name: "Sensorialist",
    tagline: "The Taste of the World",
    desc: "Luxury. Flavor. Every detail.",
    image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/06c97fccece45f84047a5e61ce5fe84252be5ec46da7d60089aab469dc54f4e5.png",
    color: "#F97316"
  },
  {
    name: "Soul Traveler",
    tagline: "The Sacred Wanderer",
    desc: "Peace. Sacred spaces. Transformation.",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80",
    color: "#06B6D4"
  }
];

export default function SoulprintSection() {
  return (
    <section data-testid="soulprint-section" className="py-20 md:py-28 px-6 md:px-12 bg-[#111111] relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-3" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, rgba(201,168,76,0.04) 0%, transparent 70%)" }} />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section intro — richer storytelling */}
        <div className="text-center mb-10 md:mb-14">
          <p className="overline mb-5">Soulprint Discovery</p>

          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-[#FAFAFA] tracking-tight mb-5">
            Discover How <span className="italic text-[#C9A84C]">You Travel.</span>
          </h2>

          <p className="font-heading text-lg md:text-xl font-light italic text-[#D4C49A] mb-5 tracking-wide max-w-2xl mx-auto">
            &ldquo;Not all journeys are the same. Yours shouldn&apos;t be either.&rdquo;
          </p>

          <p className="text-[#A1A1AA] text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Soulprint is our proprietary framework for understanding your travel personality.
            Six archetypes. One is yours. And when we know which one — every experience we design
            is built around who you truly are, not where the crowd goes.
          </p>
        </div>

        {/* Archetype cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-12">
          {ARCHETYPES.map((arch, i) => (
            <div
              key={arch.name}
              data-testid={`archetype-card-${arch.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="group relative overflow-hidden rounded-xl aspect-[3/4] cursor-pointer card-zoom"
            >
              <img src={arch.image} alt={arch.name} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />

              {/* Color top accent on hover */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                style={{ background: arch.color }}
              />

              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                <p className="text-[9px] uppercase tracking-[0.2em] mb-1" style={{ color: arch.color }}>{arch.tagline}</p>
                <p className="font-heading text-base md:text-lg font-light text-white leading-snug">{arch.name}</p>
                <p className="text-xs text-[#A1A1AA] mt-1 leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-300">{arch.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA block — richer, more psychologically engaging */}
        <div className="text-center max-w-xl mx-auto">
          {/* Intrigue line */}
          <p className="text-[#71717A] text-sm italic mb-5">
            Which one are you? Most people are surprised by the answer.
          </p>

          <Link
            to="/quiz"
            data-testid="soulprint-section-quiz-btn"
            className="btn-gold text-xs px-14 py-4 inline-block mb-4"
          >
            Discover Your Soulprint
          </Link>

          {/* Meta details */}
          <p className="text-[#71717A] text-xs uppercase tracking-[0.18em]">
            3 minutes · 10 questions · Instant archetype reveal
          </p>

          {/* Trust nudge */}
          <div className="flex items-center justify-center gap-6 mt-6">
            {["Psychologically designed", "Identity-first", "No two results identical"].map((item, i) => (
              <span key={item} className="flex items-center gap-1.5 text-[10px] text-[#71717A] uppercase tracking-[0.15em]">
                <span className="w-1 h-1 rounded-full bg-[#C9A84C]/50" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
